<template>
  <div>
    <!-- Timing -->
    <drawer-section
      title="Timing"
      icon="icon-calendar-60"
      :collapsible="true"
      :expanded.sync="sections.timing"
    >
      <div class="form-row">
        <div class="form-col">
          <base-input label="Start date" :error="errs.start_date">
            <el-date-picker
              :editable="false"
              type="date"
              placeholder="mm-dd-yyyy"
              :format="dayOfWeek + dateFormatCal"
              v-model="slotData.start_date"
              :picker-options="{ firstDayOfWeek: firstDayOfWeek }"
            />
          </base-input>
        </div>
        <div class="form-col">
          <base-input label="Start time" :error="errs.start_time">
            <el-time-picker
              v-model="slotData.start_time"
              :format="pickerTimeFormat"
              placeholder="HH:MM"
            />
          </base-input>
        </div>
      </div>

      <div class="form-row">
        <div class="form-col">
          <base-input label="End date" :error="errs.end_date">
            <el-date-picker
              :editable="false"
              type="date"
              placeholder="mm-dd-yyyy"
              :format="dayOfWeek + dateFormatCal"
              v-model="slotData.end_date"
              :picker-options="{ firstDayOfWeek: firstDayOfWeek }"
            />
          </base-input>
        </div>
        <div class="form-col">
          <base-input label="End time" :error="errs.end_time">
            <el-time-picker
              v-model="slotData.end_time"
              :format="pickerTimeFormat"
              placeholder="HH:MM"
            />
          </base-input>
        </div>
      </div>
    </drawer-section>

    <!-- Identity (resource + category) -->
    <drawer-section
      title="Identity"
      icon="icon-single-02"
      :collapsible="true"
      :expanded.sync="sections.identity"
    >
      <div class="mb-3">
        <segmented-toggle
          :value="slotData.anonymous_resource ? 'anonymous' : 'staff'"
          size="sm"
          :options="resourceModeOptions"
          @input="onResourceModeChange"
        />
      </div>

      <div v-if="slotData.anonymous_resource">
        <base-input label="Resource label" placeholder="e.g., Room A, Station 1" v-model="slotData.label" />
      </div>

      <template v-if="!slotData.anonymous_resource">
        <base-input label="Staff member">
          <el-autocomplete
            ref="staffAutocomplete"
            style="width: 100%"
            v-model="slotData.resource_assignee_value"
            :fetch-suggestions="fetchStaffSuggestions"
            placeholder="Search by name or email"
            :trigger-on-focus="false"
            @select="handleSelectStaffDirect"
          >
            <template #default="{ item }">
              <p style="margin-bottom: -12px" class="text-primary">
                {{ item.first_name }} {{ item.last_name }}
              </p>
              <small class="font-italic">
                <i class="tim-icons icon-email-85"></i> {{ item.email }}
              </small>
            </template>
          </el-autocomplete>
        </base-input>
        <p v-if="slotData.company_user_id" class="current-staff text-white">
          Currently assigned: {{ slotData.first_name }} {{ slotData.last_name }}
        </p>
      </template>

      <div class="identity-divider"></div>

      <resource-category-selector
        :company-group-id="companyGroupId"
        :initial-category-id="slotData.resource_category_id"
        :error="errs.resource_category_id"
        @category-changed="onCategoryChanged"
      />
    </drawer-section>

    <!-- Capacity -->
    <drawer-section
      title="Capacity"
      icon="icon-settings-gear-63"
      :collapsible="true"
      :expanded.sync="sections.capacity"
    >
      <div class="capacity-row">
        <div class="capacity-label">
          <span class="text-white">Assignment priority</span>
          <span class="capacity-hint">1 is the highest priority</span>
        </div>
        <div class="capacity-input capacity-input--select">
          <el-select v-model="slotData.priority" class="select-primary" placeholder="Priority" size="small">
            <el-option
              v-for="pr in priorities"
              class="select-primary"
              :label="pr.toString()"
              :value="pr"
              :key="'PR' + pr"
            />
          </el-select>
        </div>
      </div>
      <div class="capacity-row">
        <div class="capacity-label">
          <span class="text-white">Concurrent events</span>
          <span class="capacity-hint">Number of events this resource can support at the same time. If an event uses ticket-mode blocking, each ticket counts as one event toward this limit.</span>
        </div>
        <div class="capacity-input capacity-input--number">
          <el-input-number v-model="slotData.supported_event_count" :min="0" size="small" />
        </div>
      </div>
    </drawer-section>

    <!-- Blocking rules -->
    <drawer-section
      title="Blocking rules"
      icon="icon-lock-circle"
      :collapsible="true"
      :expanded.sync="sections.blocking"
    >
      <template #header-actions>
        <el-tooltip
          v-if="slotData.enable_autoblock && isCallToBook"
          content="Auto call to book"
          placement="bottom"
          effect="light"
          :open-delay="200"
        >
          <span class="blocking-icon blocking-icon--call">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
              <path fill-rule="evenodd" d="M12 1.5a5.25 5.25 0 00-5.25 5.25v3a3 3 0 00-3 3v6.75a3 3 0 003 3h10.5a3 3 0 003-3v-6.75a3 3 0 00-3-3v-3c0-2.9-2.35-5.25-5.25-5.25zm3.75 8.25v-3a3.75 3.75 0 10-7.5 0v3h7.5z" clip-rule="evenodd" />
            </svg>
          </span>
        </el-tooltip>
        <el-tooltip
          v-else-if="slotData.enable_autoblock"
          content="Auto block"
          placement="bottom"
          effect="light"
          :open-delay="200"
        >
          <span class="blocking-icon blocking-icon--block">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
              <path fill-rule="evenodd" d="M12 1.5a5.25 5.25 0 00-5.25 5.25v3a3 3 0 00-3 3v6.75a3 3 0 003 3h10.5a3 3 0 003-3v-6.75a3 3 0 00-3-3v-3c0-2.9-2.35-5.25-5.25-5.25zm3.75 8.25v-3a3.75 3.75 0 10-7.5 0v3h7.5z" clip-rule="evenodd" />
            </svg>
          </span>
        </el-tooltip>
        <el-tooltip
          v-if="slotData.enable_auto_unblock"
          content="Auto unblock"
          placement="bottom"
          effect="light"
          :open-delay="200"
        >
          <span class="blocking-icon blocking-icon--unblock">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
              <path d="M18 1.5c2.9 0 5.25 2.35 5.25 5.25v3.75a.75.75 0 01-1.5 0V6.75a3.75 3.75 0 10-7.5 0v3a3 3 0 013 3v6.75a3 3 0 01-3 3H3.75a3 3 0 01-3-3v-6.75a3 3 0 013-3h9v-3c0-2.9 2.35-5.25 5.25-5.25z" />
            </svg>
          </span>
        </el-tooltip>
        <el-tooltip
          v-if="slotData.saturate_on_first_assignment"
          content="Locked after assignment"
          placement="bottom"
          effect="light"
          :open-delay="200"
        >
          <span class="blocking-icon blocking-icon--saturate">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
              <path fill-rule="evenodd" d="M12 1.5a5.25 5.25 0 00-5.25 5.25v3a3 3 0 00-3 3v6.75a3 3 0 003 3h10.5a3 3 0 003-3v-6.75a3 3 0 00-3-3v-3c0-2.9-2.35-5.25-5.25-5.25zm3.75 8.25v-3a3.75 3.75 0 10-7.5 0v3h7.5z" clip-rule="evenodd" />
            </svg>
          </span>
        </el-tooltip>
      </template>
      <div class="toggle-row mb-3">
        <el-switch active-color="#e14eca" v-model="slotData.enable_autoblock" />
        <label class="mb-0 ml-2" @click="slotData.enable_autoblock = !slotData.enable_autoblock" style="cursor: pointer;">
          Auto-block events when capacity is insufficient
        </label>
      </div>

      <div v-if="slotData.enable_autoblock" class="settings-group mb-3">
        <base-input label="Block slots with the following status">
          <el-select
            v-model="slotData.block_status"
            class="select-primary"
            placeholder="Status"
            value-key="id"
          >
            <el-option
              v-for="(s, i) in blockStatuses"
              class="select-primary"
              :label="s.name"
              :value="{ id: s.id, name: s.name, label: s.label }"
              :key="'S' + i"
            />
          </el-select>
        </base-input>

        <div class="toggle-row mb-3">
          <el-switch active-color="#e14eca" v-model="slotData.include_start_end_time_in_blocking" />
          <label class="mb-0 ml-2" @click="slotData.include_start_end_time_in_blocking = !slotData.include_start_end_time_in_blocking" style="cursor: pointer;">
            Include event start/end time in block rule
            <el-popover popper-class="otc-pop" trigger="hover" placement="right">
              <div>
                <div class="popover-body">When enabled, slots that start or end at the same time are treated as overlapping.</div>
              </div>
              <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
            </el-popover>
          </label>
        </div>

        <div class="mb-3">
          <label class="d-block mb-2">Apply blocking rule to</label>
          <segmented-toggle
            :value="slotData.apply_to_all_events ? 'all' : 'selected'"
            size="sm"
            :options="blockScopeOptions"
            @input="onBlockScopeChange"
          />
        </div>

        <div v-if="!slotData.apply_to_all_events">
          <base-input label="Associated events">
            <el-select
              v-model="slotData.linked_games"
              class="select-primary"
              value-key="id"
              multiple
              placeholder="Select events"
            >
              <el-option
                v-for="(g, i) in games"
                class="select-primary"
                :label="g.name"
                :value="{ name: g.name, id: g.id }"
                :key="'GG' + i"
              />
            </el-select>
          </base-input>
        </div>
      </div>

      <div class="toggle-row mb-3">
        <el-switch active-color="#e14eca" v-model="slotData.enable_auto_unblock" />
        <label class="mb-0 ml-2" @click="slotData.enable_auto_unblock = !slotData.enable_auto_unblock" style="cursor: pointer;">
          Auto-unblock when bookings change
          <el-popover popper-class="otc-pop" trigger="hover" placement="right">
            <div>
              <div class="popover-body">When enabled, blocked slots will automatically become available when resource capacity allows. Choose whether it also restores call-to-book slots, or only blocked slots.</div>
            </div>
            <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
          </el-popover>
        </label>
      </div>

      <div v-if="slotData.enable_auto_unblock" class="mb-3">
        <label class="d-block mb-2">Restore</label>
        <segmented-toggle
          :value="slotData.exclude_call_to_book_from_auto_unblock ? 'blocked_only' : 'all'"
          size="sm"
          :options="unblockScopeOptions"
          @input="onUnblockScopeChange"
        />
      </div>

      <div class="toggle-row">
        <el-switch active-color="#e14eca" v-model="slotData.saturate_on_first_assignment" />
        <label class="mb-0 ml-2" @click="slotData.saturate_on_first_assignment = !slotData.saturate_on_first_assignment" style="cursor: pointer;">
          Lock resource after first assignment
          <el-popover popper-class="otc-pop" trigger="hover" placement="right">
            <div>
              <div class="popover-body">Once any booking is assigned, this resource blocks all further events even if concurrent capacity remains.</div>
            </div>
            <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
          </el-popover>
        </label>
      </div>
    </drawer-section>

    <!-- Assignments -->
    <drawer-section
      title="Assignments"
      icon="icon-badge"
      :collapsible="true"
      :expanded.sync="sections.assignment"
    >
      <template v-if="(slotData.assignment || []).length > 0" #header-actions>
        <badge type="default">{{ slotData.assignment.length }}</badge>
      </template>

      <info-hint v-if="!slotData.assignment || slotData.assignment.length === 0">
        Assign this resource to a booking to track when it's in use.
      </info-hint>

      <div v-else class="assignment-list">
        <div
          v-for="(item, index) in slotData.assignment"
          :key="(item.booking_slot_id || 'idx-') + '-' + index"
          class="assignment-row"
        >
          <div class="assignment-row__main">
            <span class="text-primary">{{ item.name }}</span>
            <badge class="ml-2">#{{ item.booking_slot_id }}</badge>
            <el-tooltip
              :content="item.is_locked ? 'Locked - will not be auto-reassigned' : 'Unlocked - can be auto-reassigned'"
              effect="light"
              :open-delay="300"
              placement="top"
            >
              <i
                @click="$emit('toggle-lock', item, index)"
                :class="item.is_locked ? 'el-icon-lock text-warning' : 'el-icon-unlock text-white'"
                class="lock-icon"
              />
            </el-tooltip>
            <div class="ml-auto">
              <button
                type="button"
                class="assignment-row__remove"
                title="Remove assignment"
                @click="$emit('remove-assignment', item, index)"
              >
                <i class="tim-icons icon-simple-remove"></i>
              </button>
            </div>
          </div>
          <div class="assignment-row__sub">
            <i class="tim-icons icon-calendar-60"></i>
            {{ getTime(item.start_time) }}-{{ getTime(item.end_time) }}
            <template v-if="!isNew && item.time_assigned">
              <span class="assignment-row__meta">assigned {{ formatLocalDateTime(item.time_assigned) }}</span>
            </template>
            <template v-if="!isNew && item.time_freed_up">
              <span class="assignment-row__meta">freed {{ formatLocalDateTime(item.time_freed_up) }}</span>
            </template>
          </div>
        </div>
      </div>

      <div v-if="editResourceBooking" class="mt-3">
        <base-input label="Search booking">
          <el-autocomplete
            style="width: 100%"
            v-model="slotData.resource_assigned_value"
            :fetch-suggestions="fetchBookingSuggestions"
            placeholder="Search by name or slot ID"
            :trigger-on-focus="false"
            @select="handleSelectBookingDirect"
          >
            <template #default="{ item }">
              <div style="margin-bottom: -12px">
                <span class="text-primary">{{ item.name }}</span>
              </div>
              <small class="font-italic">
                <i class="tim-icons icon-calendar-60"></i>
                {{ getTime(item.start_time) }}-{{ getTime(item.end_time) }}, #{{ item.id }}
              </small>
            </template>
          </el-autocomplete>
        </base-input>
        <base-button size="sm" type="secondary" @click="editResourceBooking = false">Cancel</base-button>
      </div>

      <div v-else class="mt-3">
        <base-button @click="editResourceBooking = true" type="primary" size="sm">
          <i class="tim-icons icon-simple-add mr-1"></i>
          Assign to a booking
        </base-button>
      </div>
    </drawer-section>

    <!-- Activity log nav (existing slots only) -->
    <button
      v-if="!isNew"
      type="button"
      class="nav-row"
      @click="$emit('view-activity-log')"
    >
      <span class="nav-row__left">
        <i class="tim-icons icon-bullet-list-67 mr-2"></i>
        <span class="text-white">Activity log</span>
      </span>
      <i class="tim-icons icon-minimal-right nav-row__chevron"></i>
    </button>

    <!-- Delete (existing slots, with permission) -->
    <drawer-section
      v-if="!isNew && canDelete"
      title="Delete resource slot"
      icon="icon-trash-simple"
      variant="danger"
    >
      <div class="danger-action-row">
        <span class="danger-action-hint">Permanently remove this resource slot.</span>
        <base-button variant="outlined" tone="danger" @click="$emit('delete')">
          <i class="tim-icons icon-trash-simple"></i>
          <span>Delete</span>
        </base-button>
      </div>
    </drawer-section>
  </div>
</template>

<script>
import { Badge } from '@/components';
import ResourceCategorySelector from '@/components/ResourceCategories/ResourceCategorySelector.vue';
import moment from 'moment';
import _ from 'lodash';
import { pickerDateFormat, pickerTimeFormat } from '@/plugins/dates';
import {
  TimePicker,
  DatePicker,
  Switch,
  Select,
  Option,
  Autocomplete,
  InputNumber,
  Tooltip,
  Popover
} from 'element-ui';

export default {
  name: 'ResourceDetailView',

  components: {
    Badge,
    ResourceCategorySelector,
    [TimePicker.name]: TimePicker,
    [DatePicker.name]: DatePicker,
    [Switch.name]: Switch,
    [Select.name]: Select,
    [Option.name]: Option,
    [Autocomplete.name]: Autocomplete,
    [InputNumber.name]: InputNumber,
    [Tooltip.name]: Tooltip,
    [Popover.name]: Popover
  },

  props: {
    // Renamed from `slot` to avoid Vue 2's reserved `slot` attribute,
    // which would route this component into a named slot of its parent.
    slotData: { type: Object, required: true },
    errs: { type: Object, default: () => ({}) },
    isNew: { type: Boolean, default: true },
    canEdit: { type: Boolean, default: true },
    canDelete: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    companyGroupId: { type: [Number, String], default: null },
    games: { type: Array, default: () => [] },
    blockStatuses: { type: Array, default: () => [] },
    dateFormat: { type: String, default: 'MM/DD/YYYY' },
    timeFormat: { type: String, default: 'hh:mm A' },
    firstDayOfWeek: { type: Number, default: 0 },
    priorities: { type: Array, default: () => [1, 2, 3, 4, 5] },
    fetchStaffSuggestions: { type: Function, default: () => (q, cb) => cb([]) },
    fetchBookingSuggestions: { type: Function, default: () => (q, cb) => cb([]) }
  },

  data() {
    return {
      editResourceBooking: false,
      resourceModeOptions: [
        { value: 'staff', label: 'Staff member' },
        { value: 'anonymous', label: 'Anonymous' }
      ],
      blockScopeOptions: [
        { value: 'all', label: 'All events' },
        { value: 'selected', label: 'Selected events' }
      ],
      unblockScopeOptions: [
        { value: 'all', label: 'Blocked & call to book' },
        { value: 'blocked_only', label: 'Blocked only' }
      ],
      sections: {
        timing: true,
        identity: false,
        capacity: false,
        blocking: false,
        assignment: false
      }
    };
  },

  computed: {
    dateFormatCal() {
      return pickerDateFormat(this.dateFormat);
    },
    dayOfWeek() {
      try {
        const group = JSON.parse(localStorage.getItem('group'));
        if (group && group.show_day_of_week) return 'ddd ';
      } catch (e) { /* ignore */ }
      return '';
    },
    pickerTimeFormat() {
      return pickerTimeFormat(this.timeFormat);
    },
    isCallToBook() {
      const bs = this.slotData.block_status;
      if (!bs) return false;
      return (typeof bs === 'string' ? bs : bs.label) === 'call_to_book';
    }
  },

  watch: {
    'slotData.id'() {
      this.editResourceBooking = false;
    }
  },

  methods: {
    getTime(time) {
      if (!time) return '';
      const t = time.includes && time.includes('T') ? time.split('T')[1] : time;
      if (!t) return '';
      const parts = String(t).split(':');
      if (parts.length < 2) return time;
      const h = parseInt(parts[0], 10);
      if (this.timeFormat === 'HH:mm') return `${parts[0]}:${parts[1]}`;
      const ampm = h >= 12 ? 'PM' : 'AM';
      const h12 = h % 12 || 12;
      return `${h12}:${parts[1]} ${ampm}`;
    },

    formatLocalDateTime(d) {
      if (!d) return '-';
      const fmt = this.timeFormat === 'HH:mm' ? 'HH:mm' : 'h:mm A';
      return moment(new Date(d)).format(this.dateFormat + ' ' + fmt);
    },

    onCategoryChanged(data) {
      this.slotData.resource_category_id = data.categoryId;
    },

    onResourceModeChange(value) {
      this.slotData.anonymous_resource = value === 'anonymous';
      if (this.slotData.anonymous_resource) {
        this.slotData.company_user_id = null;
        this.slotData.first_name = '';
        this.slotData.last_name = '';
        this.slotData.resource_assignee_value = '';
      }
    },

    onBlockScopeChange(value) {
      this.slotData.apply_to_all_events = value === 'all';
    },

    onUnblockScopeChange(value) {
      this.slotData.exclude_call_to_book_from_auto_unblock = value === 'blocked_only';
    },

    handleSelectStaffDirect(item) {
      this.slotData.company_user_id = item.id;
      this.slotData.first_name = item.first_name;
      this.slotData.last_name = item.last_name;
      this.slotData.label = '';
      this.slotData.resource_assignee_value = `${item.first_name} ${item.last_name}`;
    },

    handleSelectBookingDirect(item) {
      const resourceDate = moment(this.slotData.start_date).format('YYYY-MM-DD');
      const resourceEndDate = moment(this.slotData.end_date).format('YYYY-MM-DD');
      const assignment = {
        name: item.name,
        booking_date: item.booking_date || resourceDate,
        start_time: item.start_time || moment(this.slotData.start_time).format('HH:mm:ss'),
        end_date: item.end_date || resourceEndDate,
        end_time: item.end_time || moment(this.slotData.end_time).format('HH:mm:ss'),
        status: item.status,
        booking_slot_id: item.id,
        is_locked: 1
      };
      const found = _.find(this.slotData.assignment, { booking_slot_id: item.id });
      if (!found) {
        if (!this.slotData.assignment) this.slotData.assignment = [];
        this.slotData.assignment.push(assignment);
      }
      this.slotData.resource_assigned_value = '';
      this.editResourceBooking = false;
    }
  }
};
</script>

<style lang="scss" scoped>
// Form layout
.form-row {
  display: flex;
  gap: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
  }
}

.form-col {
  flex: 1;
}

.toggle-row {
  display: flex;
  align-items: center;

  label {
    color: #fff;
    font-size: 13px;
  }
}

.settings-group {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  padding: 16px;
}

.muted-text {
  color: rgba(125, 125, 125, 0.9);
}

.current-staff {
  margin-top: 8px;
  margin-bottom: 0;
  font-size: 12px;
  opacity: 0.8;
}

.identity-divider {
  height: 1px;
  background: rgba(125, 125, 125, 0.18);
  margin: 16px 0;
}

// Date/time picker containment
::v-deep .el-date-editor,
::v-deep .el-date-editor.el-input {
  width: 100% !important;
}

::v-deep .el-input-number {
  width: 100%;
}

::v-deep .el-input-number .el-input__inner {
  text-align: left;
}

::v-deep .el-select {
  width: 100%;
}

::v-deep .el-autocomplete {
  width: 100%;
}

// Capacity rows
.capacity-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 0;

  & + .capacity-row {
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }
}

.capacity-label {
  display: flex;
  flex-direction: column;
  gap: 2px;

  span:first-child {
    font-size: 13px;
    font-weight: 500;
  }
}

.capacity-hint {
  font-size: 11px;
  color: rgba(125, 125, 125, 0.9);
  line-height: 1.3;
}

.capacity-input {
  flex-shrink: 0;
}

.capacity-input--select {
  width: 120px;
  ::v-deep .el-select { width: 120px; }
}

.capacity-input--number {
  width: 120px;
  ::v-deep .el-input-number { width: 120px; }
  ::v-deep .el-input-number .el-input__inner { text-align: center; }
}

// Assignment list (replaces b-table)
.assignment-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.assignment-row {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 6px;
  padding: 10px 12px;
}

.assignment-row__main {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
}

.assignment-row__sub {
  margin-top: 4px;
  font-size: 11px;
  color: rgba(125, 125, 125, 0.9);
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.assignment-row__meta {
  font-style: italic;
}

.lock-icon {
  cursor: pointer;
  font-size: 16px;
  margin-left: 4px;
}

.assignment-row__remove {
  background: transparent;
  border: none;
  color: rgba(125, 125, 125, 0.9);
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(245, 54, 92, 0.12);
    color: #f5364c;
  }

  i { font-size: 12px; }
}

// Blocking rules status icons (mirrors resource calendar column header palette)
.blocking-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 4px;

  &--call { color: #ff8d72; }
  &--block { color: #fd5d93; }
  &--unblock { color: #00f2c3; }
  &--saturate { color: #1d8cf8; }
}

// Activity log nav row (matches drawer-section header rhythm)
.nav-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 12px 16px;
  margin-bottom: 16px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.9);
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.04);
  }
}

.nav-row__left {
  display: flex;
  align-items: center;

  i.tim-icons { font-size: 14px; }
}

.nav-row__chevron {
  font-size: 10px;
  color: rgba(125, 125, 125, 0.9);
}

// Danger section row
// .danger-action-row, .danger-action-hint, .danger-action-btn moved to global
// _drawer-actions.scss (used across multiple drawers)

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>

<style lang="scss">
// Light mode overrides
.white-content {
  .settings-group {
    background: #f0f2f5;
    border-color: #d1d5db;
  }

  .toggle-row label { color: #525f7f; }
  .capacity-hint { color: rgba(0, 0, 0, 0.45); }
  .capacity-row + .capacity-row { border-top-color: rgba(0, 0, 0, 0.08); }

  .assignment-row {
    background: #f8f9fa;
    border-color: #e3e3e3;
  }
  .assignment-row__sub { color: #6b7383; }
  .assignment-row__remove { color: #6b7383; }

  .nav-row {
    background: #f8f9fa;
    border-color: #e3e3e3;
    color: #344675;

    &:hover { background: #ebedf0; }
  }
  .nav-row__chevron { color: #9A9A9A; }

  .current-staff { color: #344675 !important; }

  .identity-divider { background: rgba(0, 0, 0, 0.08); }
}
</style>
